import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';

const ResellerRegistration = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    kvkNumber: '',
    postalCode: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.companyName?.trim()) {
      newErrors.companyName = 'Bedrijfsnaam is verplicht';
    }

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'Voornaam is verplicht';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Achternaam is verplicht';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'E-mailadres is verplicht';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Ongeldig e-mailadres';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Telefoonnummer is verplicht';
    } else if (!/^(\+31|0)[0-9]{9}$/?.test(formData?.phone?.replace(/\s/g, ''))) {
      newErrors.phone = 'Ongeldig Nederlands telefoonnummer';
    }

    if (!formData?.kvkNumber?.trim()) {
      newErrors.kvkNumber = 'KVK-nummer is verplicht';
    } else if (!/^[0-9]{8}$/?.test(formData?.kvkNumber)) {
      newErrors.kvkNumber = 'Ongeldig KVK-nummer (8 cijfers)';
    }

    if (!formData?.postalCode?.trim()) {
      newErrors.postalCode = 'Postcode is verplicht';
    } else if (!/^[1-9][0-9]{3}\s?[A-Z]{2}$/i?.test(formData?.postalCode)) {
      newErrors.postalCode = 'Ongeldige Nederlandse postcode';
    }

    if (!formData?.password) {
      newErrors.password = 'Wachtwoord is verplicht';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Wachtwoord moet minimaal 8 karakters bevatten';
    }

    if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Wachtwoorden komen niet overeen';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'U moet akkoord gaan met de voorwaarden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const { data, error } = await signUp(
        formData?.email,
        formData?.password,
        {
          firstName: formData?.firstName,
          lastName: formData?.lastName,
          fullName: `${formData?.firstName} ${formData?.lastName}`,
          phone: formData?.phone,
          postalCode: formData?.postalCode,
          companyName: formData?.companyName,
          kvkNumber: formData?.kvkNumber,
          userType: 'reseller'
        }
      );

      if (error) {
        setErrors({ submit: error?.message || 'Registratie mislukt. Probeer opnieuw.' });
        return;
      }

      navigate('/reseller-dashboard');
    } catch (error) {
      setErrors({ submit: 'Er is een fout opgetreden. Probeer het opnieuw.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      
      <div className="pt-16 lg:pt-0 lg:ml-[72px] transition-all duration-300 flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
              <Icon name="Building" size={32} color="white" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              Reseller Registratie
            </h1>
            <p className="text-muted-foreground">
              Word partner en beheer uw eigen klantportfolio
            </p>
          </div>

          {errors?.submit && (
            <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-lg">
              <p className="text-sm text-error">{errors?.submit}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Bedrijfsnaam"
              value={formData?.companyName}
              onChange={(e) => handleInputChange('companyName', e?.target?.value)}
              error={errors?.companyName}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Voornaam"
                value={formData?.firstName}
                onChange={(e) => handleInputChange('firstName', e?.target?.value)}
                error={errors?.firstName}
                required
              />
              <Input
                label="Achternaam"
                value={formData?.lastName}
                onChange={(e) => handleInputChange('lastName', e?.target?.value)}
                error={errors?.lastName}
                required
              />
            </div>

            <Input
              label="E-mailadres"
              type="email"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              required
            />

            <Input
              label="Telefoonnummer"
              value={formData?.phone}
              onChange={(e) => handleInputChange('phone', e?.target?.value)}
              error={errors?.phone}
              placeholder="06 12345678"
              required
            />

            <Input
              label="KVK-nummer"
              value={formData?.kvkNumber}
              onChange={(e) => handleInputChange('kvkNumber', e?.target?.value)}
              error={errors?.kvkNumber}
              placeholder="12345678"
              required
            />

            <Input
              label="Postcode"
              value={formData?.postalCode}
              onChange={(e) => handleInputChange('postalCode', e?.target?.value)}
              error={errors?.postalCode}
              placeholder="1234 AB"
              required
            />

            <div className="relative">
              <Input
                label="Wachtwoord"
                type={showPassword ? 'text' : 'password'}
                value={formData?.password}
                onChange={(e) => handleInputChange('password', e?.target?.value)}
                error={errors?.password}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
              </button>
            </div>

            <Input
              label="Bevestig Wachtwoord"
              type="password"
              value={formData?.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
              error={errors?.confirmPassword}
              required
            />

            <div className="flex items-start space-x-2">
              <input
                type="checkbox"
                checked={formData?.agreeToTerms}
                onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
                className="mt-1 rounded border-input"
              />
              <label className="text-sm text-muted-foreground">
                Ik ga akkoord met de{' '}
                <Link to="/terms" className="text-primary hover:underline">
                  algemene voorwaarden
                </Link>
              </label>
            </div>
            {errors?.agreeToTerms && (
              <p className="text-sm text-error">{errors?.agreeToTerms}</p>
            )}

            <Button type="submit" fullWidth loading={isLoading}>
              Registreren als Reseller
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Al een account?{' '}
            <Link to="/leverancier-login" className="text-primary hover:underline font-medium">
              Inloggen
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Benefits */}
      <div className="hidden lg:flex flex-1 bg-primary/5 items-center justify-center p-8">
        <div className="max-w-md">
          <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
            Voordelen als Reseller Partner
          </h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="CheckCircle" size={20} color="white" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Klantportfolio Beheer</h3>
                <p className="text-sm text-muted-foreground">
                  Beheer al uw klanten en projecten in één centraal systeem
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Network" size={20} color="white" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Provider Netwerk</h3>
                <p className="text-sm text-muted-foreground">
                  Toegang tot ons uitgebreide netwerk van gekwalificeerde providers
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="DollarSign" size={20} color="white" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Omzet Analytics</h3>
                <p className="text-sm text-muted-foreground">
                  Gedetailleerde inzichten in uw omzet en winstgevendheid
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ResellerRegistration;